import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Eye, Info, ShoppingCart, TrendingUp, Calendar, Tag, DollarSign, FileText, Download, X, ChevronLeft, ChevronRight, Maximize2, Presentation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import InquiryModal from '../components/InquiryModal';
import PaymentModal from '../components/PaymentModal';
import SEO, { generateIdeaStructuredData } from '../components/SEO';
import api, { trackPageView } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => {
  if (!price) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

// Check if file is a PowerPoint
const isPowerPoint = (url) => {
  if (!url) return false;
  return url.endsWith('.ppt') || url.endsWith('.pptx');
};

const IdeaDetailPage = () => {
  const { id } = useParams();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [inquiryType, setInquiryType] = useState('info');
  const [paymentType, setPaymentType] = useState('buy');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [pptViewerOpen, setPptViewerOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchIdea();
      trackPageView(`/ideas/${id}`, id, 'view');
    }
  }, [id]);

  const fetchIdea = async () => {
    try {
      const response = await api.get(`/ideas/${id}`);
      setIdea(response.data);
    } catch (error) {
      console.error('Failed to fetch idea:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (type) => {
    trackPageView(`/ideas/${id}`, id, `click_${type}`);
    if (type === 'info') {
      setInquiryType('info');
      setModalOpen(true);
    } else {
      setPaymentType(type);
      setPaymentModalOpen(true);
    }
  };

  const statusColors = {
    available: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    in_development: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    sold: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };

  const statusLabels = {
    available: 'Available',
    in_development: 'In Development',
    sold: 'Sold',
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-8 w-32 skeleton rounded mb-8" />
          <div className="h-12 w-3/4 skeleton rounded mb-4" />
          <div className="h-6 w-1/2 skeleton rounded mb-8" />
          <div className="h-64 skeleton rounded-xl" />
        </div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="idea-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-semibold text-white mb-4">Idea Not Found</h2>
          <p className="text-slate-400 mb-6">The idea you're looking for doesn't exist or has been removed.</p>
          <Link to="/ideas">
            <Button className="bg-cyan-500 hover:bg-cyan-400 text-black">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Ideas
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="idea-detail-page">
      <SEO 
        title={idea.title}
        description={idea.tagline || idea.description?.substring(0, 160)}
        url={`/ideas/${idea.id}`}
        type="article"
        image={idea.image_url ? `${BACKEND_URL}${idea.image_url}` : undefined}
        article={{
          publishedTime: idea.created_at,
          section: idea.category,
          tags: idea.tech_stack
        }}
        structuredData={generateIdeaStructuredData(idea)}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <Link to="/ideas" className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors" data-testid="back-to-ideas-link">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Ideas
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="mb-8">
              {/* Idea Image - Clickable for full size */}
              {idea.image_url && (
                <div 
                  className="mb-6 rounded-xl overflow-hidden border border-white/10 cursor-pointer group relative" 
                  data-testid="idea-image"
                  onClick={() => setImageModalOpen(true)}
                >
                  <img 
                    src={`${BACKEND_URL}${idea.image_url}`} 
                    alt={idea.title}
                    className="w-full h-auto max-h-[400px] object-contain bg-slate-900"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <Maximize2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-xs font-mono uppercase tracking-wider text-cyan-400">
                  {idea.category}
                </span>
                <Badge className={statusColors[idea.status]}>
                  {statusLabels[idea.status]}
                </Badge>
                {idea.featured && (
                  <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                    Featured
                  </Badge>
                )}
              </div>

              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-testid="idea-title">
                {idea.title}
              </h1>

              <p className="text-xl text-slate-300" data-testid="idea-tagline">
                {idea.tagline}
              </p>
            </div>

            {/* Description */}
            <div className="prose prose-invert max-w-none mb-8" data-testid="idea-description">
              <h2 className="font-heading text-xl font-semibold text-white mb-4">Overview</h2>
              <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                {idea.description}
              </p>
            </div>

            {/* Document/Presentation Attachment */}
            {idea.document_url && (
              <div className="mb-8 p-4 rounded-xl bg-slate-900/50 border border-white/10" data-testid="idea-document">
                <h2 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  {isPowerPoint(idea.document_url) ? (
                    <Presentation className="h-5 w-5 text-orange-400" />
                  ) : (
                    <FileText className="h-5 w-5 text-cyan-400" />
                  )}
                  {isPowerPoint(idea.document_url) ? 'Presentation' : 'Attached Document'}
                </h2>
                <div className="flex flex-wrap gap-3">
                  <a 
                    href={`${BACKEND_URL}${idea.document_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </a>
                  {isPowerPoint(idea.document_url) && (
                    <Button
                      onClick={() => setPptViewerOpen(true)}
                      className="bg-orange-500/10 border border-orange-500/20 text-orange-400 hover:bg-orange-500/20"
                      data-testid="view-presentation-btn"
                    >
                      <Presentation className="h-4 w-4 mr-2" />
                      View Presentation
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Tech Stack */}
            {idea.tech_stack?.length > 0 && (
              <div className="mb-8" data-testid="idea-tech-stack">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">Technology Stack</h2>
                <div className="flex flex-wrap gap-2">
                  {idea.tech_stack.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 rounded-lg bg-slate-900 text-slate-300 border border-white/10 text-sm"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Meta info */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                {idea.views || 0} views
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(idea.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                {idea.category}
              </span>
            </div>
          </div>

          {/* Sidebar - Action Buttons */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4 p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="idea-actions">
              <h3 className="font-heading font-semibold text-white mb-4">Interested?</h3>

              {/* Buy Price */}
              {idea.buy_price && (
                <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20 mb-4">
                  <span className="text-xs text-violet-400 uppercase tracking-wider flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Buy Price
                  </span>
                  <p className="text-2xl font-bold text-white">{formatPrice(idea.buy_price)}</p>
                </div>
              )}
              
              {/* Investment Price */}
              {idea.invest_price && (
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mb-4">
                  <span className="text-xs text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Investment Amount
                  </span>
                  <p className="text-2xl font-bold text-white">{formatPrice(idea.invest_price)}</p>
                </div>
              )}

              {/* Action Buttons */}
              <Button
                onClick={() => handleActionClick('info')}
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-5 shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all hover:shadow-[0_0_25px_rgba(6,182,212,0.5)]"
                data-testid="get-info-btn"
              >
                <Info className="mr-2 h-5 w-5" />
                Get Information
              </Button>

              <Button
                onClick={() => handleActionClick('buy')}
                disabled={!idea.buy_price}
                className="w-full bg-violet-500 hover:bg-violet-400 text-white font-semibold py-5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="buy-idea-btn"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {idea.buy_price ? `Buy for ${formatPrice(idea.buy_price)}` : 'Not For Sale'}
              </Button>

              <Button
                onClick={() => handleActionClick('invest')}
                disabled={!idea.invest_price}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold py-5 shadow-[0_0_15px_rgba(16,185,129,0.3)] transition-all hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                data-testid="invest-btn"
              >
                <TrendingUp className="mr-2 h-5 w-5" />
                {idea.invest_price ? `Invest ${formatPrice(idea.invest_price)}` : 'Not Accepting Investment'}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Secure payments powered by xMoney
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      <InquiryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        ideaId={idea.id}
        ideaTitle={idea.title}
        inquiryType={inquiryType}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        ideaId={idea.id}
        ideaTitle={idea.title}
        paymentType={paymentType}
        amount={paymentType === 'buy' ? idea.buy_price : idea.invest_price}
      />

      {/* Image Lightbox Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-white/10" data-testid="image-lightbox">
          <button
            onClick={() => setImageModalOpen(false)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          {idea.image_url && (
            <img 
              src={`${BACKEND_URL}${idea.image_url}`} 
              alt={idea.title}
              className="w-full h-full object-contain max-h-[85vh]"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* PowerPoint Viewer Modal */}
      <Dialog open={pptViewerOpen} onOpenChange={setPptViewerOpen}>
        <DialogContent className="max-w-[95vw] w-[1200px] h-[80vh] p-0 bg-slate-900 border-white/10" data-testid="ppt-viewer">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-heading font-semibold text-white flex items-center gap-2">
                <Presentation className="h-5 w-5 text-orange-400" />
                Presentation Viewer
              </h3>
              <div className="flex items-center gap-2">
                <a 
                  href={`${BACKEND_URL}${idea.document_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-colors text-sm"
                >
                  <Download className="h-4 w-4" />
                  Download
                </a>
                <button
                  onClick={() => setPptViewerOpen(false)}
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-950">
              {idea.document_url && (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(BACKEND_URL + idea.document_url)}`}
                  className="w-full h-full border-0"
                  title="PowerPoint Viewer"
                />
              )}
            </div>
            <div className="p-3 border-t border-white/10 text-center">
              <p className="text-xs text-slate-500">
                Powered by Microsoft Office Online Viewer. If the presentation doesn't load, please download it directly.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IdeaDetailPage;
